/**
 * React/TypeScript code for WebView to Flutter Authentication Bridge
 *
 * This file contains the code you need to paste into your React webview app
 * to communicate with the Flutter app for Google and Facebook Sign-In authentication.
 *
 * Usage:
 * 1. Copy the FlutterAuthBridge component and use it in your app
 * 2. Call requestLogin() for Google Sign-In or requestFacebookLogin() for Facebook Sign-In
 * 3. Handle the returned Firebase ID token in your app
 */

import React, { useEffect, useState } from 'react';

// Type definitions
interface FlutterAuthResponse {
  success: boolean;
  idToken: string | null;
  displayName?: string | null;
  email?: string | null;
  photoURL?: string | null;
  uid?: string | null;
  provider?: 'google' | 'facebook' | null;
  error: string | null;
}

interface FlutterInAppWebView {
  callHandler: (handlerName: string, ...args: any[]) => Promise<FlutterAuthResponse>;
}

declare global {
  interface Window {
    flutter_inappwebview?: FlutterInAppWebView;
  }
}

/**
 * Hook to check if running in Flutter WebView
 */
export const useIsFlutterWebView = (): boolean => {
  const [isFlutterWebView, setIsFlutterWebView] = useState(false);

  useEffect(() => {
    // Check if flutter_inappwebview is available
    const checkFlutterWebView = () => {
      const isFlutter = typeof window !== 'undefined' && window.flutter_inappwebview !== undefined;
      setIsFlutterWebView(isFlutter);
    };

    checkFlutterWebView();

    // Also check after a short delay in case it loads asynchronously
    const timeout = setTimeout(checkFlutterWebView, 1000);

    return () => clearTimeout(timeout);
  }, []);

  return isFlutterWebView;
};

/**
 * Request Google Sign-In from Flutter app
 * Returns the Firebase ID token if successful, null otherwise
 */
export const requestFlutterLogin = async (): Promise<string | null> => {
  try {
    // Check if we're in a Flutter WebView
    if (!window.flutter_inappwebview) {
      console.warn('Not running in Flutter WebView. Login will not work.');
      return null;
    }

    console.log('🔐 Requesting Google login from Flutter app...');

    // Call the Flutter handler
    const response = await window.flutter_inappwebview.callHandler('requestLogin');

    if (response.success && response.idToken) {
      console.log('✅ Google login successful, received ID token');
      return response.idToken;
    } else {
      console.error('❌ Google login failed:', response.error);
      return null;
    }
  } catch (error) {
    console.error('❌ Error calling Flutter Google login handler:', error);
    return null;
  }
};

/**
 * Request Facebook Sign-In from Flutter app
 * Returns the Firebase ID token if successful, null otherwise
 */
export const requestFlutterFacebookLogin = async (): Promise<any | null> => {
  try {
    // Check if we're in a Flutter WebView
    if (!window.flutter_inappwebview) {
      console.warn('Not running in Flutter WebView. Facebook login will not work.');
      return null;
    }

    console.log('🔐 Requesting Facebook login from Flutter app...');

    // Call the Flutter handler
    const response = await window.flutter_inappwebview.callHandler('requestFacebookLogin');

    if (response.success) {
      console.log('✅ Facebook login successful, received ID token');
      return response;
    } else {
      console.error('❌ Facebook login failed:', response.error);
      return null;
    }
  } catch (error) {
    console.error('❌ Error calling Flutter Facebook login handler:', error);
    return null;
  }
};

/**
 * Request login from Flutter app with full response (includes user info)
 * Returns the full auth response if successful, null otherwise
 */
export const requestFlutterLoginWithDetails = async (
  provider: 'google' | 'facebook' = 'google',
): Promise<FlutterAuthResponse | null> => {
  try {
    if (!window.flutter_inappwebview) {
      console.warn('Not running in Flutter WebView. Login will not work.');
      return null;
    }

    console.log(`🔐 Requesting ${provider} login from Flutter app...`);

    const handlerName = provider === 'facebook' ? 'requestFacebookLogin' : 'requestLogin';
    const response = await window.flutter_inappwebview.callHandler(handlerName);

    if (response.success) {
      console.log(`✅ ${provider} login successful`);
      return response;
    } else {
      console.error(`❌ ${provider} login failed:`, response.error);
      return null;
    }
  } catch (error) {
    console.error(`❌ Error calling Flutter ${provider} login handler:`, error);
    return null;
  }
};

/**
 * React component that provides authentication functionality
 * Use this in your app to trigger login
 */
export const FlutterAuthBridge: React.FC<{
  onLoginSuccess?: (user: any) => void;
  onLoginError?: (error: string) => void;
  children?: (props: {
    requestLogin: () => Promise<void>;
    isFlutterWebView: boolean;
    isLoading: boolean;
  }) => React.ReactNode;
}> = ({ onLoginSuccess, onLoginError, children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const isFlutterWebView = useIsFlutterWebView();

  const handleLogin = async () => {
    if (!isFlutterWebView) {
      const error = 'Not running in Flutter WebView. Please use the mobile app.';
      console.warn(error);
      onLoginError?.(error);
      return;
    }

    setIsLoading(true);
    try {
      const user = await requestFlutterLogin();

      if (user) {
        onLoginSuccess?.(user);
      } else {
        onLoginError?.('Login was cancelled or failed');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      onLoginError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (children) {
    return <>{children({ requestLogin: handleLogin, isFlutterWebView, isLoading })}</>;
  }

  return null;
};

/**
 * Example Google login button component
 */
export const LoginButton: React.FC<{
  onLoginSuccess: (user: any) => void;
}> = ({ onLoginSuccess }) => {
  return (
    <FlutterAuthBridge
      onLoginSuccess={onLoginSuccess}
      onLoginError={(error) => {
        console.error('Login error:', error);
        alert(`Login failed: ${error}`);
      }}
    >
      {({ requestLogin, isFlutterWebView, isLoading }) => (
        <button
          onClick={requestLogin}
          disabled={!isFlutterWebView || isLoading}
          style={{
            padding: '12px 24px',
            fontSize: 'var(--kol-button-font-size, 16px)',
            backgroundColor: isFlutterWebView ? '#4285f4' : '#ccc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isFlutterWebView && !isLoading ? 'pointer' : 'not-allowed',
          }}
        >
          {isLoading
            ? 'Signing in...'
            : isFlutterWebView
              ? 'Sign in with Google'
              : 'Please use mobile app'}
        </button>
      )}
    </FlutterAuthBridge>
  );
};

/**
 * Example Facebook login button component
 */
export const FacebookLoginButton: React.FC<{
  onLoginSuccess: (user: any) => void;
}> = ({ onLoginSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const isFlutterWebView = useIsFlutterWebView();

  const handleFacebookLogin = async () => {
    setIsLoading(true);
    try {
      const user = await requestFlutterFacebookLogin();
      if (user) {
        onLoginSuccess(user);
      }
    } catch (error) {
      console.error('Facebook login error:', error);
      alert(`Facebook login failed: ${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={handleFacebookLogin}
      disabled={!isFlutterWebView || isLoading}
      style={{
        padding: '12px 24px',
        fontSize: 'var(--kol-button-font-size, 16px)',
        backgroundColor: isFlutterWebView ? '#1877f2' : '#ccc',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: isFlutterWebView && !isLoading ? 'pointer' : 'not-allowed',
      }}
    >
      {isLoading
        ? 'Signing in...'
        : isFlutterWebView
          ? 'Sign in with Facebook'
          : 'Please use mobile app'}
    </button>
  );
};

/**
 * Hook version for more flexible usage
 */
export const useFlutterAuth = () => {
  const [isLoading, setIsLoading] = useState(false);
  const isFlutterWebView = useIsFlutterWebView();

  const login = async (): Promise<string | null> => {
    if (!isFlutterWebView) {
      console.warn('Not running in Flutter WebView');
      return null;
    }

    setIsLoading(true);
    try {
      const idToken = await requestFlutterLogin();
      return idToken;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithFacebook = async (): Promise<any | null> => {
    if (!isFlutterWebView) {
      console.warn('Not running in Flutter WebView');
      return null;
    }

    setIsLoading(true);
    try {
      const idToken = await requestFlutterFacebookLogin();
      return idToken;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithDetails = async (
    provider: 'google' | 'facebook' = 'google',
  ): Promise<FlutterAuthResponse | null> => {
    if (!isFlutterWebView) {
      console.warn('Not running in Flutter WebView');
      return null;
    }

    setIsLoading(true);
    try {
      return await requestFlutterLoginWithDetails(provider);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    login, // Google login (returns ID token only)
    loginWithFacebook, // Facebook login (returns ID token only)
    loginWithDetails, // Login with full response (includes user info)
    isLoading,
    isFlutterWebView,
  };
};

/**
 * Example usage with the hook:
 *
 * ```tsx
 * function MyComponent() {
 *   const { login, loginWithFacebook, loginWithDetails, isLoading, isFlutterWebView } = useFlutterAuth();
 *
 *   // Google login (ID token only)
 *   const handleGoogleLogin = async () => {
 *     const idToken = await login();
 *     if (idToken) {
 *       console.log('Got Google ID token:', idToken);
 *     }
 *   };
 *
 *   // Facebook login (ID token only)
 *   const handleFacebookLogin = async () => {
 *     const idToken = await loginWithFacebook();
 *     if (idToken) {
 *       console.log('Got Facebook ID token:', idToken);
 *     }
 *   };
 *
 *   // Login with full user details
 *   const handleLoginWithDetails = async (provider: 'google' | 'facebook') => {
 *     const response = await loginWithDetails(provider);
 *     if (response) {
 *       console.log('User:', response.displayName);
 *       console.log('Email:', response.email);
 *       console.log('Provider:', response.provider);
 *       console.log('ID Token:', response.idToken);
 *     }
 *   };
 *
 *   return (
 *     <div>
 *       <button onClick={handleGoogleLogin} disabled={!isFlutterWebView || isLoading}>
 *         {isLoading ? 'Signing in...' : 'Sign in with Google'}
 *       </button>
 *       <button onClick={handleFacebookLogin} disabled={!isFlutterWebView || isLoading}>
 *         {isLoading ? 'Signing in...' : 'Sign in with Facebook'}
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 */
